// Demo Data Manager - Handles default data and user overrides

// Default borrower data
const defaultBorrowerData = {
  assets: [
    {
      id: 'default-asset-1',
      type: 'commercial-property',
      name: 'Downtown Office Building',
      location: 'San Francisco, CA',
      value: 2500000,
      source: 'default',
      description: 'Class A office building in downtown San Francisco'
    },
    {
      id: 'default-asset-2',
      type: 'trucks',
      name: 'Fleet of Delivery Trucks',
      location: 'Austin, TX',
      value: 450000,
      source: 'default',
      description: '5 delivery trucks in excellent condition'
    },
    {
      id: 'default-asset-3',
      type: 'equipment',
      name: 'Manufacturing Equipment',
      location: 'Chicago, IL',
      value: 800000,
      source: 'default',
      description: 'CNC machines and production equipment'
    }
  ],
  connectedSystems: [
    { id: 'manual', connected: true }
  ],
  isDefault: true
};

// Default lender data
const defaultLenderData = {
  investmentPreferences: {
    totalAmount: 5000000,
    preferredAssetTypes: ['commercial-property', 'equipment'],
    maxLtv: 0.70,
    minInterestRate: 6.0,
    maxTerm: 36
  },
  isDefault: true
};

// Default loan offers
const defaultLoanOffers = [
  {
    id: 'default-offer-1',
    amount: 1950000, // 65% of $3M total assets
    interestRate: 6.5,
    term: '24 months',
    ltv: 0.65,
    collateral: defaultBorrowerData.assets,
    status: 'available',
    lender: 'First National Bank'
  }
];

// Default active loans (if user accepts an offer)
// eslint-disable-next-line no-unused-vars
const defaultActiveLoans = [
  {
    id: 'default-loan-1',
    amount: 1950000,
    interestRate: 6.5,
    term: '24 months',
    nextPaymentDate: '2025-02-15',
    nextPaymentAmount: 125000,
    payNowAmount: 125000,
    collateralizedAssets: ['default-asset-1', 'default-asset-2'],
    status: 'active',
    lender: 'First National Bank'
  }
];

// Cookie management functions
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    try {
      return JSON.parse(parts.pop().split(';').shift());
    } catch (e) {
      return null;
    }
  }
  return null;
};

export const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/`;
};

export const clearAllDemoData = () => {
  document.cookie = 'demo-borrower-data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'demo-lender-data=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'demo-loans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'demo-active-loans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

// Data getters with defaults
export const getBorrowerData = () => {
  const savedData = getCookie('demo-borrower-data');
  if (savedData && !savedData.isDefault) {
    return savedData; // User has customized data
  }
  
  // Return default data if no custom data exists
  if (!savedData) {
    setCookie('demo-borrower-data', defaultBorrowerData);
  }
  return defaultBorrowerData;
};

export const getLenderData = () => {
  const savedData = getCookie('demo-lender-data');
  if (savedData && !savedData.isDefault) {
    return savedData; // User has customized data
  }
  
  // Return default data if no custom data exists
  if (!savedData) {
    setCookie('demo-lender-data', defaultLenderData);
  }
  return defaultLenderData;
};

export const getLoanOffers = () => {
  const savedOffers = getCookie('demo-loans');
  if (savedOffers && savedOffers.length > 0) {
    return savedOffers; // User has custom offers
  }
  
  // Return default offers if no custom offers exist
  if (!savedOffers) {
    setCookie('demo-loans', defaultLoanOffers);
  }
  return defaultLoanOffers;
};

export const getActiveLoans = () => {
  const savedLoans = getCookie('demo-active-loans');
  if (savedLoans && savedLoans.length > 0) {
    return savedLoans; // User has active loans
  }
  
  // Return empty array by default (no active loans initially)
  return [];
};

// Data setters (these override defaults)
export const setBorrowerData = (data) => {
  const borrowerData = { ...data, isDefault: false };
  setCookie('demo-borrower-data', borrowerData);
  return borrowerData;
};

export const setLenderData = (data) => {
  const lenderData = { ...data, isDefault: false };
  setCookie('demo-lender-data', lenderData);
  return lenderData;
};

export const setLoanOffers = (offers) => {
  setCookie('demo-loans', offers);
  return offers;
};

export const setActiveLoans = (loans) => {
  setCookie('demo-active-loans', loans);
  return loans;
};

// Utility functions
export const generateLoanOffers = (assets) => {
  const totalValue = assets.reduce((sum, asset) => sum + asset.value, 0);
  const ltv = 0.65; // 65% LTV
  const loanAmount = totalValue * ltv;
  
  return [
    {
      id: `offer-${Date.now()}`,
      amount: loanAmount,
      interestRate: 6.5,
      term: '24 months',
      ltv: ltv,
      collateral: assets,
      status: 'available',
      lender: 'First National Bank'
    }
  ];
};

export const acceptLoanOffer = (offerId) => {
  const offers = getLoanOffers();
  const activeLoans = getActiveLoans();
  
  const offer = offers.find(o => o.id === offerId);
  if (!offer) return;
  
  // Create active loan from offer
  const activeLoan = {
    id: `loan-${Date.now()}`,
    amount: offer.amount,
    interestRate: offer.interestRate,
    term: offer.term,
    nextPaymentDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    nextPaymentAmount: Math.round(offer.amount * 0.065 / 12), // Monthly interest payment
    payNowAmount: Math.round(offer.amount * 0.065 / 12),
    collateralizedAssets: offer.collateral.map(a => a.id),
    status: 'active',
    lender: offer.lender
  };
  
  // Update offers and active loans
  const updatedOffers = offers.filter(o => o.id !== offerId);
  const updatedActiveLoans = [...activeLoans, activeLoan];
  
  setLoanOffers(updatedOffers);
  setActiveLoans(updatedActiveLoans);
  
  return activeLoan;
};
